import axios from 'axios';

// 프록시 설정에 맞게 수정
const USER_API_URL = '/api';                    // 프록시를 통해 User Service로
const TRADING_API_URL = '/trading-api';         // 프록시를 통해 Trading Service로  
const ANALYSIS_API_URL = '/analysis-api';       // 프록시를 통해 Analysis Service로
const RECOMMENDATION_API_URL = '/recommendation-api';  // 프록시를 통해 Recommendation Service (수정)
// 개발/프로덕션 환경 자동 감지
const isDevelopment = process.env.NODE_ENV === 'development';

// Axios 인스턴스 생성
const createAPIInstance = (baseURL, timeout = 30000) => {
  const instance = axios.create({
    baseURL,
    timeout,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // 요청 인터셉터 - JWT 토큰 자동 추가
  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      // 개발 환경에서 요청 로깅
      if (isDevelopment) {
        console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${baseURL}${config.url}`, {
          data: config.data,
          params: config.params
        });
      }
      
      return config;
    },
    (error) => Promise.reject(error)
  );

  // 응답 인터셉터
  instance.interceptors.response.use(
    (response) => {
      // 개발 환경에서 응답 로깅
      if (isDevelopment) {
        console.log(`✅ API Response: ${baseURL}${response.config.url}`, response.data);
      }
      return response;
    },
    (error) => {
      // 401 에러 시 자동 로그아웃 (로그인 요청 제외)
      if (error.response?.status === 401 && !error.config?.url?.includes('/auth/login')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
      
      // 에러 로깅
      if (isDevelopment) {
        console.error('❌ API Error:', {
          url: `${baseURL}${error.config?.url}`,
          status: error.response?.status,
          message: error.message,
          data: error.response?.data
        });
      }
      
      return Promise.reject(error);
    }
  );

  return instance;
};

// API 인스턴스 생성
const userAPI = createAPIInstance(USER_API_URL);
const tradingAPI = createAPIInstance(TRADING_API_URL);
const analysisAPI = createAPIInstance(ANALYSIS_API_URL);
const recommendationAPI = createAPIInstance(RECOMMENDATION_API_URL);

// User Service API
export const userService = {
  // 인증
  register: (userData) => userAPI.post('/auth/register', userData),
  
  login: async (credentials) => {
    try {
      const response = await userAPI.post('/auth/login', credentials);
      
      console.log('🔍 [DEBUG] 로그인 응답 전체:', response.data);
      console.log('🔍 [DEBUG] response.data.user_id:', response.data.user_id);
      console.log('🔍 [DEBUG] response.data.user:', response.data.user);
      console.log('🔍 [DEBUG] response.data.user?.id:', response.data.user?.id);
      
      // 토큰이 응답에 있으면 저장
      if (response.data && response.data.access_token) {
        const accessToken = response.data.access_token;
        localStorage.setItem('token', accessToken);
        console.log('✅ [DEBUG] 토큰 저장 완료');
        
        // JWT 토큰에서 user_id 추출
        try {
          // JWT 토큰은 "header.payload.signature" 형태
          const tokenParts = accessToken.split('.');
          if (tokenParts.length === 3) {
            // payload 부분을 base64 디코딩
            const payload = JSON.parse(atob(tokenParts[1]));
            console.log('🔑 [DEBUG] JWT payload:', payload);
            
            // 'sub' 클레임에서 user_id 추출
            if (payload.sub) {
              const userId = payload.sub;
              localStorage.setItem('user_id', userId);
              console.log('✅ [DEBUG] JWT에서 user_id 추출 성공:', userId);
            }
          }
        } catch (jwtError) {
          console.error('❌ [DEBUG] JWT 파싱 오류:', jwtError);
        }
        
        // 폴백: 응답에서 user_id 찾기
        let savedUserId = localStorage.getItem('user_id');
        if (!savedUserId) {
          if (response.data.user_id) {
            savedUserId = response.data.user_id;
            localStorage.setItem('user_id', response.data.user_id);
            console.log('✅ [DEBUG] user_id 저장 (response.data.user_id):', savedUserId);
          } else if (response.data.user && response.data.user.id) {
            savedUserId = response.data.user.id;
            localStorage.setItem('user_id', response.data.user.id);
            console.log('✅ [DEBUG] user_id 저장 (response.data.user.id):', savedUserId);
          } else {
            console.error('❌ [DEBUG] user_id를 찾을 수 없음!');
            console.log('🔍 [DEBUG] 응답 구조:', Object.keys(response.data));
          }
        }
        
        // 사용자 정보가 있으면 저장
        if (response.data.user) {
          localStorage.setItem('user', JSON.stringify(response.data.user));
          console.log('✅ [DEBUG] user 객체 저장 완료');
        }
        
        // 최종 확인
        const finalUserId = localStorage.getItem('user_id');
        console.log('🎯 [DEBUG] 최종 localStorage user_id:', finalUserId);
        
        if (!finalUserId) {
          console.error('❌ [DEBUG] user_id 저장 실패! /auth/me API 호출 필요');
        }
      }

      // 대기 중인 투자 쇼임 동의 저장 (신규 가입 후 첫 로그인 시에만 해당)
      const pendingVersion = localStorage.getItem('pending_disclaimer_version');
      if (pendingVersion) {
        try {
          await userAPI.post('/disclaimer/agree', { version: pendingVersion });
          localStorage.removeItem('pending_disclaimer_version');
          console.log('✅ 투자 쇼임 확인서 동의 저장 완료:', pendingVersion);
        } catch (e) {
          console.warn('⚠️ 동의 저장 실패 (다음 로그인 시 재시도):', e);
        }
      }

      return response;
    } catch (error) {
      console.error('❌ Login error:', error);
      throw error;
    }
  },

  logout: async () => {
    try {
      await userAPI.post('/auth/logout');
    } catch (error) {
      console.warn('로그아웃 API 호출 실패:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  },

  getMe: () => userAPI.get('/auth/me'),
  
  // 이메일 인증
  verifyEmail: (code) => userAPI.post('/auth/verify-email-code', { code }),
  verifyEmailCode: (data) => userAPI.post('/auth/verify-email-code', data),
  resendVerificationCode: async (data) => {
    console.log('📧 resendVerificationCode 호출, 데이터:', data);
    console.log('📧 데이터 타입:', typeof data);
    
    // 데이터 타입에 따른 처리
    let payload;
    if (typeof data === 'string') {
      payload = { email: data };
      console.log('📧 문자열로 받음, 객체로 변환:', payload);
    } else {
      payload = data;
      console.log('📧 객체로 받음:', payload);
    }
    
    try {
      const response = await userAPI.post('/auth/resend-verification-code', payload);
      console.log('✅ 재발송 성공:', response.data);
      return response;
    } catch (error) {
      console.error('❌ 재발송 실패:', error);
      console.error('❌ 에러 응답:', error.response?.data);
      throw error;
    }
  },
  
  // API 키 관리
  getApiKeysStatus: () => userAPI.get('/api-keys/status'),
  saveApiKeys: (apiData) => userAPI.post('/api-keys/', apiData),
  updateKisAppKey: (keyValue) => userAPI.put('/api-keys/kis-app-key/', { key_value: keyValue }),
  updateKisAppSecret: (keyValue) => userAPI.put('/api-keys/kis-app-secret/', { key_value: keyValue }),
  // updateChatGptKey: (keyValue) => userAPI.put('/api-keys/chatgpt-key', { key_value: keyValue }),
  // updateClaudeKey: (keyValue) => userAPI.put('/api-keys/claude-key', { key_value: keyValue }),
  // updatePerplexityKey: (keyValue) => userAPI.put('/api-keys/perplexity-key', { key_value: keyValue }),
  
  // KIS 토큰
  getKisTokenInfo: () => userAPI.get('/kis-token/info'),
  refreshKisToken: () => userAPI.post('/kis-token/refresh'),
  
  // AI 투자 설정
  getAiInvestmentSettings: () => userAPI.get('/ai-investment/settings'),
  saveAiInvestmentSettings: (settings) => userAPI.post('/ai-investment/settings', settings),
  
  // 계정 관리
  getCurrentUser: () => userAPI.get('/auth/me'),
  deleteAccount: (deleteData) => userAPI.post('/auth/delete-account', deleteData),

  // MCP 토큰 관리
  generateMcpToken: (data) => userAPI.post('/mcp/generate-token', data),
  getMcpTokens: () => userAPI.get('/mcp/tokens'),
  revokeMcpToken: (tokenId) => userAPI.delete(`/mcp/tokens/${tokenId}`),

  // 섹터 설정
  getSectors: () => userAPI.get('/user/sectors'),
  updateSectors: (sectorIds, isSelected) => userAPI.patch('/user/sectors', { sector_ids: sectorIds, is_selected: isSelected }),
  selectAllSectors: () => userAPI.post('/user/sectors/select-all'),
  deselectAllSectors: () => userAPI.post('/user/sectors/deselect-all'),

  // 관심 종목
  getWatchlist: () => userAPI.get('/user/watchlist'),
  addWatchlist: (data) => userAPI.post('/user/watchlist', data),
  removeWatchlist: (stockCode) => userAPI.delete(`/user/watchlist/${stockCode}`),
  updateWatchlistMemo: (stockCode, memo) => userAPI.patch(`/user/watchlist/${stockCode}/memo`, { memo }),

  // 투자 책임 확인서
  getDisclaimer: () => userAPI.get('/disclaimer/current'),
  agreeDisclaimer: (version) => userAPI.post('/disclaimer/agree', { version }),
  getMyAgreements: () => userAPI.get('/disclaimer/my-agreements'),
  checkNeedAgreement: () => userAPI.get('/disclaimer/need-agreement'),

  // 포트폴리오 우선순위 즉시 재생성 (일반 사용자)
  refreshPortfolio: () => userAPI.post('/user/portfolio/refresh'),

  // 관리자 - Top 100 재선정
  triggerScoring: () => userAPI.post('/admin/scoring/run-now'),
  getScoringStatus: () => userAPI.get('/admin/scoring/status'),
};

// Trading Service API
export const tradingService = {
  // 계좌 정보
  getAccountBalance: async () => {
    try {
      const response = await tradingAPI.get('/account/balance');
      return response.data;
    } catch (error) {
      console.error('계좌 조회 오류:', error);
      throw error;
    }
  },

  getAccountInfo: async () => {
    try {
      const response = await tradingAPI.get('/account/info');
      return response.data;
    } catch (error) {
      console.error('계좌 정보 조회 오류:', error);
      throw error;
    }
  },
  
  // 주식 거래
  searchStock: async (query) => {
    try {
      console.log(`🔍 종목 검색 요청: ${query}`);
      const response = await tradingAPI.get(`/trade/search/${encodeURIComponent(query)}`);
      console.log(`🔍 종목 검색 응답:`, response.data);
      return response.data;
    } catch (error) {
      console.error('종목 검색 오류:', error);
      throw error;
    }
  },

  getStockPrice: async (stockCode) => {
    try {
      console.log(`💰 현재가 조회 요청: ${stockCode}`);
      const response = await tradingAPI.get(`/trade/price/${stockCode}`);
      console.log(`💰 현재가 조회 응답:`, response.data);
      return response.data;
    } catch (error) {
      console.error('현재가 조회 오류:', error);
      throw error;
    }
  },

  placeOrder: async (orderData) => {
    try {
      console.log(`📋 주문 요청:`, orderData);
      const response = await tradingAPI.post('/trade/order', orderData);
      console.log(`📋 주문 응답:`, response.data);
      return response.data;
    } catch (error) {
      console.error('주문 실행 오류:', error);
      throw error;
    }
  },
  
  // 거래 내역
  getTradeHistory: async () => {
    try {
      const response = await tradingAPI.get('/trade/history');
      return response.data;
    } catch (error) {
      console.warn('거래 내역 조회 미구현:', error);
      return { success: true, data: [] };
    }
  },

  getOrders: async () => {
    try {
      const response = await tradingAPI.get('/trade/orders');
      return response.data;
    } catch (error) {
      console.warn('주문 목록 조회 미구현:', error);
      return { success: true, data: [] };
    }
  },

  // 보유 종목 조회
  getHoldings: async () => {
    try {
      const response = await tradingAPI.get('/holdings');
      return response.data;
    } catch (error) {
      console.error('보유 종목 조회 오류:', error);
      throw error;
    }
  },

  // 목표가/손절가 업데이트
  updateTargetPrices: async (stockCode, updateData) => {
    try {
      const response = await tradingAPI.put(`/holdings/${stockCode}/target-prices`, updateData);
      return response.data;
    } catch (error) {
      console.error('목표가/손절가 업데이트 오류:', error);
      throw error;
    }
  },
};

// Analysis Service API
export const analysisService = {
  // 스케줄러 상태
  getSchedulerStatus: () => analysisAPI.get('/scheduler/status'),
  
  // 포트폴리오 (화면조회용 데이터 불러오는데 사용)
  getPortfolio: () => analysisAPI.get('/api/stocks/portfolio'),
  refreshPortfolio: () => analysisAPI.post('/api/stocks/portfolio/refresh'),
  
  // ✨ 새로 추가: 우선순위 재평가
  rebalancePortfolio: () => analysisAPI.post('/api/portfolio/generate-priority'),
  
  // ✨ 새로 추가: 수동 매매 실행  
  executeManualTrading: () => analysisAPI.post('/api/trading/execute-manual'),
  
  // 시장 데이터
  getMarketSummary: () => analysisAPI.get('/api/stocks/market-summary'),
  
  // AI 분석
  analyzeStock: (symbol) => analysisAPI.get(`/api/stocks/analyze/${symbol}`),
  getAIRecommendations: () => analysisAPI.get('/api/recommendations/latest'), // 원래는 '/analysis-api/api/ai/recommendations' 였음
  
  // ✨ 새로 추가: AI 포트폴리오 우선순위 API
  getAIPortfolioPriority: (user_id) => analysisAPI.get(`/api/portfolio/priority/${user_id}`),
  
  // ✨ 새로 추가: 매매 계획 조회 API
  getPortfolioTradingPlan: (user_id) => analysisAPI.get(`/api/portfolio/trading-plan/${user_id}`),
  
  getAIAnalysis: () => analysisAPI.get('/api/ai/analysis'),
  
  // 자동매매 로그
  getExecutionLogs: () => analysisAPI.get('/api/trading/execution-logs'),
  getRiskAlerts: () => analysisAPI.get('/api/trading/risk-alerts'),
};
// Recommendation Service API
export const recommendationService = {
  // 당일 섹터 분석
  runDailyAnalysis: () => recommendationAPI.post('/analysis/manual'),
  
  // ✅ URL에 직접 파라미터 포함
  runSectorAnalysis: (sector) => 
    recommendationAPI.post(`/analysis/manual?sector=${encodeURIComponent(sector)}`),
  
  getAvailableSectors: () => recommendationAPI.get('/sectors'),
  getStatus: () => recommendationAPI.get('/health'),
};

// 헬스체크 API
export const healthCheck = {
  user: async () => {
    try {
      const response = await userAPI.get('/health');
      return { service: 'user', status: 'healthy', data: response.data };
    } catch (error) {
      return { service: 'user', status: 'unhealthy', error: error.message };
    }
  },

  trading: async () => {
    try {
      const response = await tradingAPI.get('/health');
      return { service: 'trading', status: 'healthy', data: response.data };
    } catch (error) {
      return { service: 'trading', status: 'unhealthy', error: error.message };
    }
  },

  analysis: async () => {
    try {
      const response = await analysisAPI.get('/health');
      return { service: 'analysis', status: 'healthy', data: response.data };
    } catch (error) {
      return { service: 'analysis', status: 'unhealthy', error: error.message };
    }
  },
};

// 간단한 에러 처리 유틸리티
export const getErrorMessage = (error) => {
  if (error.response?.data?.detail) {
    return error.response.data.detail;
  }
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.response?.data?.error) {
    return error.response.data.error;
  }
  if (error.message) {
    return error.message;
  }
  return '알 수 없는 오류가 발생했습니다.';
};

// 하위 호환성을 위한 handleAPIError 함수
export const handleAPIError = (error, defaultMessage = '오류가 발생했습니다.') => {
  const message = getErrorMessage(error) || defaultMessage;
  return {
    message,
    type: 'error',
    severity: 'error'
  };
};

export default {
  userService,
  tradingService,
  analysisService,
  recommendationService,
  healthCheck,
  getErrorMessage,
  handleAPIError,
};